/**
 * StudentBulkImport.js
 * Standalone student bulk import modal — extracted from BulkImportHub.js
 * with premium UI and its own CSS.
 */

import React, { useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { FaCloudUploadAlt, FaFileCsv, FaDownload, FaEye, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import configData from '../../configure';
import { useAuth } from '../../AuthContext';
import './StudentBulkImport.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

const createGeneratedId = (typeCode, index, startingNumber = 1) => {
    const year = new Date().getFullYear().toString().slice(-2);
    const admissionNumber = String(Number(startingNumber) + (index - 1)).padStart(3, '0');
    return `${typeCode}${year}${admissionNumber}`;
};

const parseDate = (dateStr) => {
    if (dateStr === null || dateStr === undefined || dateStr === '') return null;
    const value = String(dateStr).trim();
    if (!value) return null;

    if (/^\d+(\.\d+)?$/.test(value)) {
        const excelSerial = Number(value);
        const excelEpoch = new Date(Date.UTC(1899, 11, 30));
        const parsed = new Date(excelEpoch.getTime() + excelSerial * 86400000);
        return isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(value)) {
        const [day, month, yearRaw] = value.split('/');
        const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
        const parsed = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        return isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d.toISOString();
};

const normalizePhone = (value) => String(value || '').replace(/\D/g, '').slice(-10);

const createGeneratedEmail = (firstName, lastName) => {
    const fn = String(firstName || 'user').toLowerCase().replace(/[^a-z0-9]/g, '');
    const ln = String(lastName || 'name').toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${fn}.${ln}@sevabharathi.com`;
};

const createGeneratedParentEmail = (name, relation, rowNumber) => {
    const slug = String(name || relation || 'parent')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '.')
        .replace(/^\.+|\.+$/g, '') || 'parent';
    return `${slug}.${relation.toLowerCase()}.r${rowNumber}@sevabharathi.com`;
};

const normalizeGender = (gender) => {
    const value = String(gender || '').trim().toUpperCase();
    if (value === 'MALE' || value === 'FEMALE' || value === 'OTHER') return value;
    return 'OTHER';
};

const normalizeImportHeader = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeBloodGroup = (bg) => {
    if (!bg) return null;
    const value = String(bg).trim().toUpperCase().replace(/[^A-Z0-9+-]/g, '');
    if (value === 'A+' || value === 'APOSITIVE' || value === 'APOS') return 'A_POSITIVE';
    if (value === 'A-' || value === 'ANEGATIVE' || value === 'ANEG') return 'A_NEGATIVE';
    if (value === 'B+' || value === 'BPOSITIVE' || value === 'BPOS') return 'B_POSITIVE';
    if (value === 'B-' || value === 'BNEGATIVE' || value === 'BNEG') return 'B_NEGATIVE';
    if (value === 'O+' || value === 'OPOSITIVE' || value === 'OPOS') return 'O_POSITIVE';
    if (value === 'O-' || value === 'ONEGATIVE' || value === 'ONEG') return 'O_NEGATIVE';
    if (value === 'AB+' || value === 'ABPOSITIVE' || value === 'ABPOS') return 'AB_POSITIVE';
    if (value === 'AB-' || value === 'ABNEGATIVE' || value === 'ABNEG') return 'AB_NEGATIVE';
    return null;
};

const FIELD_ALIASES = {
    firstName:                     ['first name', 'firstname'],
    middleName:                    ['middle name', 'middlename'],
    lastName:                      ['last name', 'lastname'],
    gender:                        ['gender', 'sex'],
    fatherName:                    ['father name', 'fathername'],
    fatherOccupation:              ['father occupation', 'occupation', 'father occupation/name'],
    motherName:                    ['mother name', 'mothername'],
    motherOccupation:              ['mother occupation', 'occupatin', 'mother occupation/name'],
    dateOfBirth:                   ['date of birth', 'dob', 'birth date'],
    mobileNo:                      ['mobile no', 'mobile number', 'phone number', 'phone', 'contact number'],
    area:                          ['area', 'location', 'address area'],
    caste:                         ['caste', 'cast', 'category', 'caste category'],
    schoolName:                    ['school name', 'previous school', 'school', 'previous school details'],
    singleParent:                  ['single parent'],
    
    // New fields matching backend JSON
    email:                         ['email', 'email address', 'gmail', 'emailid'],
    bloodGroup:                    ['blood group', 'bloodgroup', 'bg'],
    religion:                      ['religion'],
    nationality:                   ['nationality'],
    placeOfBirth:                  ['place of birth', 'placeofbirth', 'birth place', 'pob'],
    rollNumber:                    ['roll number', 'roll number', 'rollno', 'roll'],
    admissionId:                   ['admission id', 'admission number', 'admissionno', 'admissionid'],
    dateOfJoining:                 ['admission date', 'joining date', 'date of joining', 'doj'],
    lastFourDigitsOfAadhaarNumber: ['aadhaar', 'aadhaar card', 'aadhaar number', 'aadhaar (last 4 digits)', 'aadhar'],
    addressLine1:                  ['address line 1', 'addressline1', 'address 1'],
    addressLine2:                  ['address line 2', 'addressline2', 'address 2'],
    city:                          ['city'],
    state:                         ['state'],
    pinCode:                       ['pin code', 'pincode', 'postal code', 'zip'],
    alternatePhoneNumber:          ['alternate phone no', 'alternate phone number', 'alternate contact number', 'alternate contact', 'alt mobile'],
    scholarshipAwards:             ['scholarship awards', 'scholarships', 'scholarship'],
    transportOpted:                ['transport opted', 'transport', 'bus opted'],
};

const getFieldValue = (row, aliases) => {
    const normalized = Object.entries(row || {}).reduce((acc, [key, val]) => {
        acc[normalizeImportHeader(key)] = val;
        return acc;
    }, {});
    for (const alias of aliases) {
        const norm = normalizeImportHeader(alias);
        if (Object.prototype.hasOwnProperty.call(normalized, norm)) return normalized[norm];
    }
    return '';
};

const parseCsvText = (csvText) => {
    const parsed = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => String(h || '').trim(),
    });
    if (parsed.errors?.length) throw new Error(parsed.errors[0].message || 'Failed to parse CSV');
    return parsed.data || [];
};

// ── Standard Fields Definition ─────────────────────────────────────────────────

const STANDARD_FIELDS = [
    { key: 'firstName',                     label: 'First Name',                    required: true  },
    { key: 'middleName',                    label: 'Middle Name',                   required: false },
    { key: 'lastName',                      label: 'Last Name',                     required: true  },
    { key: 'mobileNo',                      label: 'Mobile No',                     required: true  },
    { key: 'fatherName',                    label: 'Father Name',                   required: false },
    { key: 'motherName',                    label: 'Mother Name',                   required: false },
    { key: 'gender',                        label: 'Gender',                        required: true  },
    { key: 'dateOfBirth',                   label: 'Date of Birth',                 required: true  },
    { key: 'caste',                         label: 'Caste (Category)',              required: false },
    { key: 'area',                          label: 'Area',                          required: false },
    { key: 'fatherOccupation',              label: 'Father Occupation',              required: false },
    { key: 'motherOccupation',              label: 'Mother Occupation',              required: false },
    { key: 'schoolName',                    label: 'School Name',                    required: false },
    
    // New fields
    { key: 'email',                         label: 'Gmail',                         required: true  },
    { key: 'bloodGroup',                    label: 'Blood Group',                   required: false },
    { key: 'religion',                      label: 'Religion',                      required: false },
    { key: 'nationality',                   label: 'Nationality',                   required: false },
    { key: 'placeOfBirth',                  label: 'Place of Birth',                required: false },
    { key: 'rollNumber',                    label: 'Roll Number',                   required: false },
    { key: 'admissionId',                   label: 'Admission Number',              required: false },
    { key: 'dateOfJoining',                 label: 'Admission Date',                required: false },
    { key: 'lastFourDigitsOfAadhaarNumber', label: 'Last 4 Digits of Aadhaar',      required: false },
    { key: 'addressLine1',                  label: 'Address Line 1',                required: false },
    { key: 'addressLine2',                  label: 'Address Line 2',                required: false },
    { key: 'city',                          label: 'City',                          required: false },
    { key: 'state',                         label: 'State',                         required: false },
    { key: 'pinCode',                       label: 'Pin Code',                      required: false },
    { key: 'alternatePhoneNumber',          label: 'Alternate Contact Number',      required: false },
    { key: 'scholarshipAwards',             label: 'Scholarship Awards',             required: false },
    { key: 'transportOpted',                label: 'Transport Opted',               required: false },
];

const DEFAULT_ENABLED = new Set([
    'firstName', 'lastName', 'mobileNo', 'fatherName', 'motherName',
    'gender', 'dateOfBirth', 'caste', 'area', 'fatherOccupation',
    'motherOccupation', 'schoolName', 'email'
]);

const SAMPLE_VALUES = {
    firstName: 'Prasanna',
    middleName: 'Kumar',
    lastName: 'Pole',
    mobileNo: '9553133517',
    fatherName: 'Gopi',
    motherName: 'Sumaltha',
    gender: 'Female',
    dateOfBirth: '20/04/2015',
    caste: 'SC',
    area: 'Anna Nagar',
    fatherOccupation: 'Painter',
    motherOccupation: 'House Wife',
    schoolName: 'Govt School',
    email: 'prasanna.pole@example.com',
    bloodGroup: 'O+',
    religion: 'Hindu',
    nationality: 'Indian',
    placeOfBirth: 'Nellore',
    rollNumber: '101',
    admissionId: 'S26001',
    dateOfJoining: '01/06/2024',
    lastFourDigitsOfAadhaarNumber: '1234',
    addressLine1: 'H.No 1-23, Gopi Nivas',
    addressLine2: 'Gandhi Bazar',
    city: 'Nellore',
    state: 'Andhra Pradesh',
    pinCode: '524001',
    alternatePhoneNumber: '9876543210',
    scholarshipAwards: 'N/A',
    transportOpted: 'No',
};

// ── Main Component ─────────────────────────────────────────────────────────────

export default function StudentBulkImport({ classes, orgId, onClose, onImportComplete }) {
    const { axios, token } = useAuth();

    // Step: 1=configure, 2=upload, 3=result
    const [step, setStep] = useState(1);

    // Step 1 state
    const [selectedClassId, setSelectedClassId] = useState('');
    const [startingAdmNo, setStartingAdmNo] = useState(1);
    const [enabledFields, setEnabledFields] = useState(new Set(DEFAULT_ENABLED));
    const [customFields, setCustomFields] = useState([]);
    const [newCustomField, setNewCustomField] = useState('');

    // Step 2 state
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);

    // Step 3 state
    const [stats, setStats] = useState(null);

    // ── Toggle field chip ───────────────────────────────────────────────────────
    const toggleField = (key, required) => {
        if (required) return;
        setEnabledFields(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    // ── Active columns list ─────────────────────────────────────────────────────
    const activeStandard = STANDARD_FIELDS.filter(f => enabledFields.has(f.key));
    const activeColumns = [
        ...activeStandard.map(f => f.label),
        ...customFields.map(f => f.label),
    ];

    // ── Download template ───────────────────────────────────────────────────────
    const downloadTemplate = () => {
        const headers = activeColumns.join(',');
        const sampleRow = [
            ...activeStandard.map(f => SAMPLE_VALUES[f.key] || ''),
            ...customFields.map(() => 'Value Here'),
        ].join(',');
        const content = `${headers}\n${sampleRow}`;
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'students_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ── File selection ──────────────────────────────────────────────────────────
    const handleFileChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const lower = f.name.toLowerCase();
        if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
            toast.error('Please upload a CSV or Excel (.xlsx/.xls) file.');
            return;
        }
        setFile(f);
    };

    // ── Send welcome email ──────────────────────────────────────────────────────
    const sendWelcomeEmail = async (userId, email) => {
        try {
            const emailData = {
                subject: 'Welcome to Egurukul - Your Account Details',
                messageBody: `Welcome to Egurukul!\n\nYour account has been created.\n\nLogin: ${email}\nPassword: egurukul@123456\n\nPlease change your password on first login.`,
                userType: 'STUDENT',
                specificUserIds: [userId],
            };
            const fd = new FormData();
            fd.append('request', new Blob([JSON.stringify(emailData)], { type: 'application/json' }));
            await axios.post(configData.SEND_EMAIL_NOTIFICATION, fd, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            });
        } catch (e) { console.warn('Welcome email failed', e); }
    };

    // ── Core import logic ───────────────────────────────────────────────────────
    const runImport = async () => {
        if (!file || !selectedClassId) return;
        setImporting(true);

        try {
            let rows = [];
            const lower = file.name.toLowerCase();

            if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
                const buffer = await file.arrayBuffer();
                const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });
            } else {
                const text = await file.text();
                rows = parseCsvText(text);
            }

            if (!rows.length) throw new Error('No student rows found in the file.');

            // Validate required columns
            const firstRow = rows[0] || {};
            const presentHeaders = new Set(Object.keys(firstRow).map(normalizeImportHeader));
            const requiredKeys = ['firstName', 'lastName', 'mobileNo', 'gender', 'dateOfBirth', 'email'];
            const missing = requiredKeys.filter(k =>
                !FIELD_ALIASES[k].some(alias => presentHeaders.has(normalizeImportHeader(alias)))
            );
            if (missing.length) {
                throw new Error('Missing required columns: First Name, Last Name, Mobile No, Gender, Date of Birth, Gmail');
            }

            const statsObj = { total: rows.length, success: 0, duplicates: 0, failed: 0, errors: [] };
            let batchIdx = 1;

            for (let i = 0; i < rows.length; i++) {
                const row = rows[i] || {};
                const rowNumber = i + 2;

                try {
                    const firstName       = String(getFieldValue(row, FIELD_ALIASES.firstName) || '').trim();
                    const middleName      = String(getFieldValue(row, FIELD_ALIASES.middleName) || '').trim();
                    const lastName        = String(getFieldValue(row, FIELD_ALIASES.lastName) || '').trim();
                    const fatherName      = String(getFieldValue(row, FIELD_ALIASES.fatherName) || '').trim();
                    const fatherOcc       = String(getFieldValue(row, FIELD_ALIASES.fatherOccupation) || '').trim();
                    const motherName      = String(getFieldValue(row, FIELD_ALIASES.motherName) || '').trim();
                    const motherOcc       = String(getFieldValue(row, FIELD_ALIASES.motherOccupation) || '').trim();
                    const caste           = String(getFieldValue(row, FIELD_ALIASES.caste) || '').trim();
                    const area            = String(getFieldValue(row, FIELD_ALIASES.area) || '').trim();
                    const schoolName      = String(getFieldValue(row, FIELD_ALIASES.schoolName) || '').trim();
                    
                    const genderRaw       = String(getFieldValue(row, FIELD_ALIASES.gender) || '').trim();
                    if (!genderRaw) {
                        throw new Error('Missing required value: Gender');
                    }
                    const gender          = normalizeGender(genderRaw);

                    const singleParent    = String(getFieldValue(row, FIELD_ALIASES.singleParent) || '').trim().toUpperCase() === 'YES';
                    const phoneNumber     = normalizePhone(getFieldValue(row, FIELD_ALIASES.mobileNo));

                    if (!firstName || !lastName || !phoneNumber) {
                        throw new Error('Missing required values: First Name, Last Name, Mobile No');
                    }

                    const email           = String(getFieldValue(row, FIELD_ALIASES.email) || '').trim();
                    if (!email) {
                        throw new Error('Missing required value: Gmail');
                    }

                    const bloodGroup      = normalizeBloodGroup(getFieldValue(row, FIELD_ALIASES.bloodGroup));
                    const religion        = String(getFieldValue(row, FIELD_ALIASES.religion) || '').trim();
                    const nationality     = String(getFieldValue(row, FIELD_ALIASES.nationality) || 'Indian').trim();
                    const placeOfBirth    = String(getFieldValue(row, FIELD_ALIASES.placeOfBirth) || 'Unknown').trim();
                    const rollNumber      = String(getFieldValue(row, FIELD_ALIASES.rollNumber) || phoneNumber.slice(-4) || '').trim();

                    let admissionId       = String(getFieldValue(row, FIELD_ALIASES.admissionId) || '').trim();
                    if (!admissionId) {
                        admissionId       = createGeneratedId('S', batchIdx, startingAdmNo);
                    }

                    const now             = new Date().toISOString();
                    const dojParsed       = parseDate(getFieldValue(row, FIELD_ALIASES.dateOfJoining));
                    const dateOfJoining   = dojParsed || now;

                    const aadhaarRaw      = String(getFieldValue(row, FIELD_ALIASES.lastFourDigitsOfAadhaarNumber) || '').replace(/\D/g, '').slice(-4);
                    const lastFourDigitsOfAadhaarNumber = aadhaarRaw ? parseInt(aadhaarRaw, 10) : null;

                    const addressLine1    = String(getFieldValue(row, FIELD_ALIASES.addressLine1) || area || 'Unknown').trim();
                    const addressLine2    = String(getFieldValue(row, FIELD_ALIASES.addressLine2) || '').trim();
                    const city            = String(getFieldValue(row, FIELD_ALIASES.city) || area || 'Unknown').trim();
                    const state           = String(getFieldValue(row, FIELD_ALIASES.state) || 'Unknown').trim();
                    const pinCode         = String(getFieldValue(row, FIELD_ALIASES.pinCode) || '000000').trim();
                    const alternatePhoneNumber = normalizePhone(getFieldValue(row, FIELD_ALIASES.alternatePhoneNumber));

                    const scholarshipAwards = String(getFieldValue(row, FIELD_ALIASES.scholarshipAwards) || '').trim();
                    const transportRaw    = String(getFieldValue(row, FIELD_ALIASES.transportOpted) || '').trim().toLowerCase();
                    const transportOpted  = transportRaw === 'yes' || transportRaw === 'true';

                    // Parse any custom fields added dynamically by user
                    const userCustomAttrs = {};
                    customFields.forEach(cf => {
                        const rawVal = getFieldValue(row, [cf.label]);
                        if (rawVal !== undefined && rawVal !== null && rawVal !== '') {
                            userCustomAttrs[cf.label] = String(rawVal).trim();
                        }
                    });

                    const dobRaw          = getFieldValue(row, FIELD_ALIASES.dateOfBirth);
                    const parsedDob       = parseDate(dobRaw);
                    if (!parsedDob) {
                        throw new Error('Missing or invalid required value: Date of Birth');
                    }
                    const dob             = parsedDob;

                    const payload = {
                        user: {
                            creationTime: now, createdBy: 'admin',
                            lastUpdatedTime: now, lastUpdatedBy: 'admin',
                            orgId, userType: 'STUDENT',
                            email, phoneNumber, firstName, middleName, lastName,
                            gender, dateOfBirth: dob,
                            placeOfBirth, bloodGroup,
                            nationality, religion,
                            category: caste,
                            lastFourDigitsOfAadhaarNumber,
                            userStatus: 'ACTIVE',
                            additionalAttributes: {
                                ...(schoolName ? { schoolName } : {}),
                                ...(area       ? { area }       : {}),
                                ...(caste      ? { caste }      : {}),
                                ...userCustomAttrs
                            },
                            addressDetails: [{
                                addressType: 'CURRENT',
                                addressLine1,
                                addressLine2,
                                city,
                                state,
                                pinCode,
                                country: 'India',
                                alternatePhoneNumber,
                            }],
                        },
                        studentInfo: {
                            creationTime: now, createdBy: 'admin',
                            lastUpdatedTime: now, lastUpdatedBy: 'admin',
                            classId: selectedClassId,
                            admissionId,
                            rollNumber,
                            enrollmentStatus: 'ENROLLED',
                            admissionType: 'REGULAR',
                            dateOfJoining,
                            previousSchoolDetails: schoolName,
                            scholarshipAwards,
                            transportOpted,
                        },
                        parentInfo: null, facultyInfo: null, staffInfo: null,
                    };

                    const response = await axios.post(configData.REGISTER_USER, payload, {
                        headers: { accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    });

                    const studentUserId = response.data?.userId || response.data?.user?.userId;
                    if (!studentUserId) throw new Error('Student created but userId not returned');

                    // Register parents
                    const parentPayloads = [];
                    if (fatherName) {
                        const parts = fatherName.trim().split(/\s+/);
                        parentPayloads.push({
                            user: { orgId, userType: 'PARENT', email: createGeneratedParentEmail(fatherName, 'father', rowNumber), phoneNumber, firstName: parts[0], lastName: parts.slice(1).join(' ') || parts[0], userStatus: 'ACTIVE' },
                            parentInfo: { parentType: 'FATHER', occupation: fatherOcc + (singleParent ? ' (Single Parent)' : ''), listOfChildIds: [studentUserId] },
                        });
                    }
                    if (motherName) {
                        const parts = motherName.trim().split(/\s+/);
                        parentPayloads.push({
                            user: { orgId, userType: 'PARENT', email: createGeneratedParentEmail(motherName, 'mother', rowNumber), phoneNumber, firstName: parts[0], lastName: parts.slice(1).join(' ') || parts[0], userStatus: 'ACTIVE' },
                            parentInfo: { parentType: 'MOTHER', occupation: motherOcc + (singleParent ? ' (Single Parent)' : ''), listOfChildIds: [studentUserId] },
                        });
                    }

                    if (parentPayloads.length) {
                        const parentResponses = await Promise.all(parentPayloads.map(pp =>
                            axios.post(configData.REGISTER_USER, pp, {
                                headers: { accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            })
                        ));
                        const parentIds = parentResponses.map(r => r.data?.userId || r.data?.user?.userId).filter(Boolean);
                        if (parentIds.length) {
                            const existing = (await axios.get(`${configData.GET_USER_DETAILS}/${studentUserId}`, { headers: { Authorization: `Bearer ${token}` } })).data;
                            await axios.put(`${configData.UPDATE_USER}/${studentUserId}`, {
                                user: existing.user,
                                studentInfo: { ...existing.studentInfo, listOfParentIds: parentIds },
                                parentInfo: existing.parentInfo || null,
                                facultyInfo: existing.facultyInfo || null,
                                staffInfo: existing.staffInfo || null,
                            }, {
                                headers: { accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            });
                        }
                    }

                    sendWelcomeEmail(studentUserId, email);
                    statsObj.success += 1;
                    batchIdx += 1;
                } catch (err) {
                    console.error(`Row ${rowNumber} failed:`, err.response?.data || err.message);
                    statsObj.failed += 1;
                    const reason = err.response?.data?.errors
                        ? err.response.data.errors.map(e => `${e.field}: ${e.defaultMessage}`).join(', ')
                        : (err.response?.data?.message || err.message);
                    statsObj.errors.push({ row: rowNumber, reason: String(reason) });
                }
            }

            setStats(statsObj);
            setStep(3);
            if (statsObj.success > 0) toast.success(`✅ Imported ${statsObj.success} student(s) successfully!`);
            if (statsObj.failed > 0)  toast.error(`❌ ${statsObj.failed} student(s) failed. Check errors below.`);
            if (onImportComplete) onImportComplete(statsObj);
        } catch (err) {
            toast.error('Import failed: ' + err.message);
        } finally {
            setImporting(false);
        }
    };

    // ── Result helpers ──────────────────────────────────────────────────────────
    const resultIconClass = () => {
        if (!stats) return '';
        if (stats.failed === 0) return 'success';
        if (stats.success > 0)  return 'partial';
        return 'failed';
    };

    const resultEmoji  = () => ({ success: '✅', partial: '⚠️', failed: '❌' }[resultIconClass()] || '');
    const resultTitle  = () => ({
        success: `${stats.success} student(s) imported successfully!`,
        partial: `${stats.success} imported, ${stats.failed} failed`,
        failed:  'Import failed — no students were added',
    }[resultIconClass()] || '');

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <div className="sbi-overlay" onClick={(e) => e.target.classList.contains('sbi-overlay') && onClose()}>
            <div className="sbi-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="sbi-header">
                    <div className="sbi-header-left">
                        <div className="sbi-header-icon">🎓</div>
                        <div>
                            <p className="sbi-header-title">Import Students</p>
                            <p className="sbi-header-sub">Upload CSV or Excel file to register students in bulk</p>
                        </div>
                    </div>
                    <button className="sbi-close-btn" onClick={onClose}>×</button>
                </div>

                {/* Progress Steps */}
                <div className="sbi-progress-bar">
                    <div className={`sbi-step ${step >= 1 ? (step > 1 ? 'done' : 'active') : ''}`}>
                        <div className="sbi-step-dot">{step > 1 ? '✓' : '1'}</div>
                        <span className="sbi-step-label">Configure</span>
                    </div>
                    <div className={`sbi-step-connector ${step > 1 ? 'done' : ''}`} />
                    <div className={`sbi-step ${step >= 2 ? (step > 2 ? 'done' : 'active') : ''}`}>
                        <div className="sbi-step-dot">{step > 2 ? '✓' : '2'}</div>
                        <span className="sbi-step-label">Upload</span>
                    </div>
                    <div className={`sbi-step-connector ${step > 2 ? 'done' : ''}`} />
                    <div className={`sbi-step ${step >= 3 ? 'active' : ''}`}>
                        <div className="sbi-step-dot">3</div>
                        <span className="sbi-step-label">Result</span>
                    </div>
                </div>

                {/* Body */}
                <div className="sbi-body">

                    {/* ── STEP 1: Configure ── */}
                    {step === 1 && (
                        <>
                            <div className="sbi-section-header">
                                <p className="sbi-section-title">Configure Your Import</p>
                                <p className="sbi-section-desc">Select the class, set the admission number sequence, and choose which fields to include.</p>
                            </div>

                            {/* Class + Adm No */}
                            <div className="sbi-config-row">
                                <div className="sbi-form-group flex-2">
                                    <label className="sbi-form-label">Select Class *</label>
                                    <select className="sbi-select" value={selectedClassId} onChange={e => setSelectedClassId(e.target.value)}>
                                        <option value="">— Choose a class —</option>
                                        {classes.map(c => (
                                            <option key={c.classId} value={c.classId}>
                                                {c.className} {c.sectionName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sbi-form-group flex-1">
                                    <label className="sbi-form-label" title="e.g. 1 → S26001">Admission No. Start</label>
                                    <input
                                        type="number"
                                        className="sbi-input"
                                        min="1"
                                        value={startingAdmNo}
                                        onChange={e => setStartingAdmNo(Math.max(1, parseInt(e.target.value) || 1))}
                                    />
                                </div>
                            </div>

                            {/* Standard Fields */}
                            <div className="sbi-fields-section">
                                <div className="sbi-subsection-label">Standard Fields</div>
                                <div className="sbi-chips-grid">
                                    {STANDARD_FIELDS.map(field => {
                                        const on = enabledFields.has(field.key);
                                        const cls = [
                                            'sbi-field-chip',
                                            field.required ? 'chip-required' : (on ? 'chip-on' : 'chip-off'),
                                        ].join(' ');
                                        return (
                                            <div
                                                key={field.key}
                                                className={cls}
                                                onClick={() => toggleField(field.key, field.required)}
                                                title={field.required ? 'Required — cannot be removed' : (on ? 'Click to remove' : 'Click to add')}
                                            >
                                                <span className="sbi-chip-icon">
                                                    {field.required ? '★' : (on ? '✓' : '+')}
                                                </span>
                                                <span>{field.label}</span>
                                                {field.required && <span className="sbi-req-star">*</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Custom Fields */}
                            <div className="sbi-fields-section">
                                <div className="sbi-subsection-label">
                                    Custom Fields
                                    <span style={{ fontSize: '11px', fontWeight: 400, color: '#aaa', marginLeft: 6 }}>stored in Additional Details</span>
                                </div>
                                <div className="sbi-custom-add">
                                    <input
                                        type="text"
                                        className="sbi-custom-input"
                                        placeholder="e.g. Blood Group, Height, Religion..."
                                        value={newCustomField}
                                        onChange={e => setNewCustomField(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' && newCustomField.trim()) {
                                                setCustomFields(prev => [...prev, { label: newCustomField.trim() }]);
                                                setNewCustomField('');
                                            }
                                        }}
                                    />
                                    <button
                                        className="sbi-custom-add-btn"
                                        disabled={!newCustomField.trim()}
                                        onClick={() => {
                                            setCustomFields(prev => [...prev, { label: newCustomField.trim() }]);
                                            setNewCustomField('');
                                        }}
                                    >+ Add</button>
                                </div>
                                {customFields.length > 0 && (
                                    <div className="sbi-custom-tags">
                                        {customFields.map((cf, i) => (
                                            <div key={i} className="sbi-custom-tag">
                                                <span>{cf.label}</span>
                                                <button
                                                    className="sbi-custom-tag-remove"
                                                    onClick={() => setCustomFields(prev => prev.filter((_, idx) => idx !== i))}
                                                >×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Column Preview */}
                            <div className="sbi-preview-box">
                                <div className="sbi-preview-label">📄 Your CSV will have these columns</div>
                                <div className="sbi-preview-cols">
                                    {activeColumns.map((col, i) => (
                                        <span key={i} className="sbi-preview-col">{col}</span>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── STEP 2: Upload ── */}
                    {step === 2 && !importing && (
                        <>
                            <div className="sbi-upload-info">
                                Upload a <strong>CSV or Excel</strong> file with these columns:<br />
                                <strong>{activeColumns.join(' · ')}</strong>
                            </div>

                            <div className="sbi-upload-zone" onClick={() => document.getElementById('sbi-file-input').click()}>
                                <input
                                    id="sbi-file-input"
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />
                                {file ? (
                                    <div className="sbi-file-selected">
                                        <FaFileCsv />
                                        <span>{file.name}</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="sbi-upload-icon"><FaCloudUploadAlt /></div>
                                        <p>Click to select CSV or Excel file</p>
                                        <small>Supports .csv, .xlsx, .xls</small>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {/* ── IMPORTING spinner ── */}
                    {step === 2 && importing && (
                        <div className="sbi-processing">
                            <div className="sbi-processing-spinner" />
                            <p className="sbi-processing-text">Importing students…</p>
                            <p className="sbi-processing-sub">Please wait, this may take a moment</p>
                        </div>
                    )}

                    {/* ── STEP 3: Result ── */}
                    {step === 3 && stats && (
                        <div className="sbi-result">
                            <div className={`sbi-result-icon ${resultIconClass()}`}>
                                {resultEmoji()}
                            </div>
                            <p className="sbi-result-title">{resultTitle()}</p>

                            <div className="sbi-stats-row">
                                <div className="sbi-stat-card success">
                                    <span className="sbi-stat-number">{stats.success}</span>
                                    <span className="sbi-stat-label">Imported</span>
                                </div>
                                <div className="sbi-stat-card warning">
                                    <span className="sbi-stat-number">{stats.duplicates || 0}</span>
                                    <span className="sbi-stat-label">Duplicates</span>
                                </div>
                                <div className="sbi-stat-card error">
                                    <span className="sbi-stat-number">{stats.failed || 0}</span>
                                    <span className="sbi-stat-label">Failed</span>
                                </div>
                            </div>

                            {stats.errors?.length > 0 && (
                                <div className="sbi-error-list">
                                    <p className="sbi-error-list-title">Error Details (last 5)</p>
                                    <ul>
                                        {stats.errors.slice(-5).map((err, i) => (
                                            <li key={i}>Row {err.row}: {err.reason}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sbi-footer">
                    {step === 1 && (
                        <>
                            <button className="sbi-btn secondary" onClick={onClose}>Cancel</button>
                            <button className="sbi-btn outline" onClick={downloadTemplate}>
                                <FaDownload /> Download Template
                            </button>
                            <button
                                className="sbi-btn primary"
                                disabled={!selectedClassId}
                                onClick={() => setStep(2)}
                            >
                                Next: Upload File →
                            </button>
                        </>
                    )}

                    {step === 2 && !importing && (
                        <>
                            <button className="sbi-back-link" onClick={() => setStep(1)}>← Back</button>
                            <button className="sbi-btn secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="sbi-btn primary"
                                disabled={!file}
                                onClick={runImport}
                            >
                                Start Import
                            </button>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <button className="sbi-btn secondary" onClick={onClose}>Close</button>
                            <button className="sbi-btn outline" onClick={() => { setStep(1); setFile(null); setStats(null); }}>
                                Import More
                            </button>
                            <button
                                className="sbi-btn success-btn"
                                onClick={() => onImportComplete && onImportComplete(stats, true)}
                            >
                                <FaEye /> View Students
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
